import React, { useState } from 'react';
import { Card, Table, Tag, Statistic, Row, Col, Button, DatePicker, Space, Breadcrumb, Spin } from 'antd';
import { ArrowLeftOutlined, HistoryOutlined, PlusCircleOutlined, MinusCircleOutlined, InboxOutlined, DollarOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useFacilities } from '../../../hooks/useFacilities';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const FacilityDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const {
        useGetFacilityById,
        useGetFacilityHistory,
    } = useFacilities();

    const [historyFilters, setHistoryFilters] = useState({
        FacilityId: id,
        PageNumber: 1,
        PageSize: 10,
    });

    const { data: facility, isLoading: facilityLoading } = useGetFacilityById(id);
    const { data: historyData, isLoading: historyLoading } = useGetFacilityHistory(historyFilters);

    const handleDateRangeChange = (dates) => {
        if (dates) {
            setHistoryFilters({
                ...historyFilters,
                StartDate: dates[0].format('YYYY-MM-DD'),
                EndDate: dates[1].format('YYYY-MM-DD'),
                PageNumber: 1,
            });
        } else {
            const { StartDate, EndDate, ...rest } = historyFilters;
            setHistoryFilters(rest);
        }
    };

    const historyColumns = [
        {
            title: <span className="text-white font-semibold">Date & Time</span>,
            dataIndex: 'createdAt',
            width: 180,
            render: (date) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-800">
                        {dayjs(date).format('MMM DD, YYYY')}
                    </span>
                    <span className="text-xs text-gray-500">
                        {dayjs(date).format('hh:mm A')}
                    </span>
                </div>
            ),
        },
        {
            title: <span className="text-white font-semibold">Transaction Type</span>,
            dataIndex: 'type',
            width: 150,
            align: 'center',
          render: (type) => {
    const isProduction = type === 1;
    return (
      <span className={`text-sm font-semibold flex items-center gap-1 ${isProduction ? 'text-green-600' : 'text-orange-500'}`}>
    {isProduction ? <PlusCircleOutlined /> : <MinusCircleOutlined />}
    {isProduction ? 'Production' : 'Consumption'}
</span>
    );
},
        },
        {
            title: <span className="text-white font-semibold">Amount</span>,
            dataIndex: 'amount',
            align: 'center',
          render: (amount, record) => {
    const isProduction = record.type === 1;
    return (
        <div className={`font-bold text-lg ${isProduction ? 'text-green-600' : 'text-orange-600'}`}>
           {amount}
        </div>
    );
},
        },
        {
            title: <span className="text-white font-semibold">Price/Unit</span>,
            dataIndex: 'price',
            align: 'center',
            render: (price) => (
                <div className="font-semibold text-gray-700">
                    EGP {price?.toFixed(2) || '0.00'}
                </div>
            ),
        },
        {
            title: <span className="text-white font-semibold">Total Cost</span>,
            dataIndex: 'totalCost',
            align: 'center',
            render: (_, record) => {
                const total = (record.amount || 0) * (record.price || 0);
                return (
                    <div className="px-3 py-2 bg-blue-50 rounded-lg">
                        <span className="font-bold text-blue-700 text-base">
                            ${total.toFixed(2)}
                        </span>
                    </div>
                );
            },
        },
        {
            title: <span className="text-white font-semibold">Supplier</span>,
            dataIndex: 'supplierName',
            render: (name) => (
                <span className="text-gray-700 font-medium">
                    {name || 'N/A'}
                </span>
            ),
        },
    
    ];

    if (facilityLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spin size="large" />
            </div>
        );
    }

    if (!facility) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen gap-4">
                <h2 className="text-2xl font-bold text-gray-600">Facility not found</h2>
                <Button 
                    type="primary" 
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/facilities')}
                    size="large"
                >
                    Back to Facilities
                </Button>
            </div>
        );
    }

    const stockPercentage = facility.initAmount > 0 
        ? (facility.currentAmount / facility.initAmount) * 100 
        : 0;

    return (
        <div className="p-6 space-y-6">
            {/* Breadcrumb */}
            <Breadcrumb
                items={[
                    {
                        title: (
                            <a onClick={() => navigate('/facilities')} className="cursor-pointer hover:text-primary">
                                Facilities
                            </a>
                        ),
                    },
                    {
                        title: facility.name,
                    },
                ]}
                className="text-base"
            />

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button 
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/facilities')}
                        size="large"
                        className="shadow-md"
                    >
                        Back
                    </Button>
                    <div className='flex gap-1  '>
                        <h1 className="text-3xl font-bold text-gray-800 mb-1">{facility.name}</h1>
                        <span color="cyan" className="px-3 py-1  text-primary rounded-full text-sm font-medium">
                            {facility.categoryName}
                        </span>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            {/* <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 -m-6 mb-4">
                            <Statistic
                                title={<span className="text-white text-opacity-90">Initial Stock</span>}
                                value={facility.initAmount}
                                prefix={<InboxOutlined className="text-white" />}
                                valueStyle={{ color: 'white', fontWeight: 'bold' }}
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
                        <div className={`bg-gradient-to-br ${
                            stockPercentage < 30 ? 'from-red-500 to-red-600' :
                            stockPercentage < 60 ? 'from-orange-500 to-orange-600' :
                            'from-green-500 to-green-600'
                        } p-4 -m-6 mb-4`}>
                            <Statistic
                                title={<span className="text-white text-opacity-90">Current Stock</span>}
                                value={facility.currentAmount}
                                prefix={<InboxOutlined className="text-white" />}
                                suffix={<span className="text-sm">/ {facility.initAmount}</span>}
                                valueStyle={{ color: 'white', fontWeight: 'bold' }}
                            />
                        </div>
                        <div className="text-center mt-2">
                            <span className="text-sm text-gray-600 font-medium">
                                {stockPercentage.toFixed(1)}% Remaining
                            </span>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 -m-6 mb-4">
                            <Statistic
                                title={<span className="text-white text-opacity-90">Total Transactions</span>}
                                value={historyData?.totalCount || 0}
                                prefix={<HistoryOutlined className="text-white" />}
                                valueStyle={{ color: 'white', fontWeight: 'bold' }}
                            />
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-4 -m-6 mb-4">
                            <Statistic
                                title={<span className="text-white text-opacity-90">Stock Status</span>}
                                value={
                                    facility.currentAmount === 0 ? 'Out' :
                                    facility.currentAmount < 5 ? 'Low' : 'Good'
                                }
                                valueStyle={{ color: 'white', fontWeight: 'bold', fontSize: '28px' }}
                            />
                        </div>
                    </Card>
                </Col>
            </Row> */}

            {/* History Table */}
            <Card 
                className="border-0 shadow-lg rounded-2xl"
                title={
                    <div className="flex items-center gap-3">
                        <HistoryOutlined className="text-2xl text-primary" />
                        <span className="text-xl font-bold">Transaction History</span>
                    </div>
                }
                extra={
                    <RangePicker 
                        onChange={handleDateRangeChange}
                        format="MMM DD, YYYY"
                        size="large"
                        className="shadow-md"
                    />
                }
            >
                <style>
                    {`  
                        .ant-table-thead > tr > th {
                            background: linear-gradient(135deg, #1F6F7D 0%, #1F6F7D 100%) !important;
                            color: white !important;
                            font-weight: 600 !important;
                            border: none !important;
                            padding: 16px !important;
                            font-size: 14px !important;
                        }
                        .ant-table-thead > tr > th::before {
                            display: none !important;
                        }
                        .ant-table-tbody > tr > td {
                            padding: 16px !important;
                            border-bottom: 1px solid #f0f0f0 !important;
                        }
                        .ant-table-tbody > tr:hover > td {
                            background: #f8fafc !important;
                        }
                    `}
                </style>
                <Table
                    columns={historyColumns}
                    dataSource={historyData?.items || []}
                    loading={historyLoading}
                    rowKey="id"
                    scroll={{ x: 1200 }}
                    pagination={{
                        current: historyFilters.PageNumber,
                        pageSize: historyFilters.PageSize,
                        total: historyData?.totalCount || 0,
                        showTotal: (total) => `Total ${total} transactions`,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        className: 'px-4 py-4',
                        onChange: (page, pageSize) => {
                            setHistoryFilters({
                                ...historyFilters,
                                PageNumber: page,
                                PageSize: pageSize,
                            });
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export default FacilityDetails;